import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, message, Input, Tabs, Row, Col, Badge, Modal } from 'antd';
import { TeamOutlined, SearchOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { myGroupApi, accountApi, clientApi, tradingApi } from '../services/api';
import { useAuth } from '../utils/auth';

// 我的分组：按当前登录账号关联的客户展示数据（超级用户展示所有），无需选择分组
export default function MyGroupPage() {
  const { user } = useAuth();
  const [allClients, setAllClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [assetStats, setAssetStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');
  const [syncing, setSyncing] = useState(false);

  // 搜索过滤
  const [accountSearch, setAccountSearch] = useState('');
  const [posSearch, setPosSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [quoteMap, setQuoteMap] = useState({});

  // 成员弹窗
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  const isSuper = user?.is_super_admin === 1 || user?.role === 'admin';
  // 成员客户端ID：超级用户展示所有（在线客户端集合）；普通用户由后端过滤，从接口返回的客户去重得到
  const myClientIds = useMemo(() => {
    if (isSuper) return allClients.map(c => c.id);
    const ids = new Set();
    accounts.forEach(a => a.client_id && ids.add(a.client_id));
    positions.forEach(p => p.client_id && ids.add(p.client_id));
    orders.forEach(o => o.client_id && ids.add(o.client_id));
    return [...ids];
  }, [isSuper, allClients, accounts, positions, orders]);

  const loadClients = async () => {
    try {
      const res = await clientApi.getOnline();
      if (res.success) setAllClients(res.data || []);
    } catch { /* ignore */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [acctRes, posRes, ordRes, statRes] = await Promise.all([
        myGroupApi.getAccounts(),
        myGroupApi.getPositions(),
        myGroupApi.getOrders(),
        accountApi.getAssetStats(),
      ]);
      if (acctRes.success) setAccounts(acctRes.data || []);
      if (posRes.success) {
        const data = posRes.data || [];
        setPositions(data);
        const codes = [...new Set(data.map(p => p.stock_code).filter(Boolean).map(c => String(c).padStart(6, '0')))];
        if (codes.length > 0) {
          tradingApi.getQuotes(codes).then(qres => {
            if (qres.success) {
              const m = {};
              (qres.data || []).forEach(q => { if (q.stock_code) m[q.stock_code] = q; });
              setQuoteMap(m);
            }
          }).catch(() => {});
        }
      }
      if (ordRes.success) setOrders(ordRes.data || []);
      if (statRes.success) setAssetStats(statRes.data || {});
    } catch (e) { console.error('[MyGroupPage] loadData error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadClients();
    loadData();
  }, [user]);

  const getClientName = (id) => {
    const c = allClients.find(x => x.id === id);
    return c ? c.name || id : id;
  };

  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts;
    const s = accountSearch.toLowerCase();
    return accounts.filter(a =>
      (a.client_id || '').toLowerCase().includes(s) ||
      (a.client_name || getClientName(a.client_id) || '').toLowerCase().includes(s)
    );
  }, [accounts, accountSearch, allClients]);

  const filteredPositions = useMemo(() => {
    if (!posSearch) return positions;
    const s = posSearch.toLowerCase();
    return positions.filter(p =>
      (p.client_id || '').toLowerCase().includes(s) ||
      (getClientName(p.client_id) || '').toLowerCase().includes(s) ||
      (p.stock_code || '').toLowerCase().includes(s) ||
      (p.stock_name || '').toLowerCase().includes(s)
    );
  }, [positions, posSearch, allClients]);

  const filteredOrders = useMemo(() => {
    if (!orderSearch) return orders;
    const s = orderSearch.toLowerCase();
    return orders.filter(o =>
      (o.client_id || '').toLowerCase().includes(s) ||
      (getClientName(o.client_id) || '').toLowerCase().includes(s) ||
      (o.stock_code || '').toLowerCase().includes(s) ||
      (o.stock_name || '').toLowerCase().includes(s)
    );
  }, [orders, orderSearch, allClients]);

  const posColumns = [
    { title: '客户端', dataIndex: 'client_id', width: 120, render: v => {
      const name = getClientName(v);
      return <Tag>{name === v ? v : `${name}(${v})`}</Tag>;
    }},
    { title: '股票', key: 'stock', width: 120, render: (_, r) => <span><b>{r.stock_name}</b><br /><small>{r.stock_code}</small></span> },
    { title: '数量', dataIndex: 'quantity', width: 70, render: v => v?.toLocaleString() },
    { title: '可用', dataIndex: 'available_qty', width: 70, render: v => v?.toLocaleString() },
    { title: '成本价', dataIndex: 'cost_price', width: 80, render: v => v?.toFixed(3) },
    { title: '市价', key: 'live_price', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      if (q) {
        const color = q.change_percent > 0 ? '#e74c3c' : q.change_percent < 0 ? '#27ae60' : '#333';
        return <span style={{ fontWeight: 600, color }}>{q.current_price.toFixed(2)}</span>;
      }
      return r.current_price?.toFixed(2) || '-';
    }},
    { title: '市值', key: 'live_mv', width: 90, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      return (price * (r.quantity || 0)).toLocaleString();
    }},
    { title: '盈亏', key: 'live_pl', width: 90, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      const qty = r.quantity || 0;
      const pl = cost > 0 ? (price - cost) * qty : (r.profit_loss || 0);
      return (
        <span style={{ color: pl >= 0 ? '#e74c3c' : '#27ae60' }}>
          {pl >= 0 ? '+' : ''}{pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      );
    }},
    { title: '盈亏比', key: 'live_plr', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      if (cost > 0 && q) {
        const ratio = (price - cost) / cost * 100;
        return <span style={{ color: ratio >= 0 ? '#e74c3c' : '#27ae60' }}>{ratio >= 0 ? '+' : ''}{ratio.toFixed(2)}%</span>;
      }
      return '-';
    }},
  ];

  const orderColumns = [
    { title: '客户端', dataIndex: 'client_id', width: 120, render: (v, r) => {
      const name = r.client_name || getClientName(v);
      return <Tag>{name === v ? v : `${name}(${v})`}</Tag>;
    }},
    { title: '日期', dataIndex: 'order_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', width: 50, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '价格', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) || '市价' },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '成交', dataIndex: 'filled_qty', width: 65, render: v => v?.toLocaleString() || 0 },
    { title: '状态', dataIndex: 'status', width: 65, render: v => {
      const colorMap = { '已成': 'success', '已撤': 'default', '已报': 'processing', '部成': 'warning', '废单': 'error' };
      const color = Object.entries(colorMap).find(([k]) => v?.includes(k))?.[1] || 'default';
      return <Tag color={color}>{v || '-'}</Tag>;
    }},
  ];

  // 统一使用后端返回的资产统计（避免前端与后端口径不一致）
  const totalMarketValue = assetStats.total_market_value || 0;
  const totalPL = assetStats.total_pl || 0;

  const onlineCount = assetStats.online_count || 0;
  const clientCount = assetStats.client_count || myClientIds.length;

  return (
    <div>
      <Card
        title={<Space>
          <TeamOutlined />
          {isSuper ? '全部客户' : '我的分组'}
          <Tag color="green">{onlineCount}在线</Tag>
          <Tag>{clientCount - onlineCount}离线</Tag>
        </Space>}
        size="small"
        loading={loading}
        extra={
          <Space>
            <Button size="small" icon={<SearchOutlined />} onClick={() => setShowMemberModal(true)}>查看客户 ({clientCount})</Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => { loadData(); loadClients(); }}>刷新</Button>
          </Space>
        }
      >
        {/* 同步按钮 */}
        <div style={{ marginBottom: 12 }}>
          <Button size="small" icon={<SyncOutlined />} loading={syncing}
            onClick={async () => {
              if (onlineCount === 0) { message.warning('当前没有在线客户端'); return; }
              setSyncing(true);
              message.loading({ content: '正在同步资金持仓...', key: 'myGroupSync' });
              try {
                const res = await myGroupApi.syncAll();
                message.destroy('myGroupSync');
                if (res.success) {
                  const { synced = 0, errors = [], total = 0, details = [] } = res.data || {};
                  const posEmpty = details.filter(d => d && d.position_synced === false).length;
                  const warnText = [];
                  if (errors.length) warnText.push(`${errors.length} 台失败`);
                  if (posEmpty) warnText.push(`${posEmpty} 台持仓未同步`);
                  message.success(`同步完成：成功 ${synced}/${total}${warnText.length ? `（${warnText.join('，')}）` : ''}`);
                  if (details.length) console.log('同步明细:', details);
                  setTimeout(() => loadData(), 2000);
                } else {
                  message.error(res.message || '同步失败');
                }
              } catch {
                message.destroy('myGroupSync');
                message.error('同步请求失败');
              }
              setSyncing(false);
            }}
          >一键同步资金持仓</Button>
        </div>

        {/* 统计卡片 */}
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col span={4}><Card size="small"><b>总资产</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: '#2980b9' }}>{(assetStats.total_asset || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
          <Col span={4}><Card size="small"><b>可用资金</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: '#27ae60' }}>{(assetStats.total_available || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
          <Col span={4}><Card size="small"><b>持仓市值</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{totalMarketValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
          <Col span={4}><Card size="small"><b>持仓盈亏</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: totalPL >= 0 ? '#e74c3c' : '#27ae60' }}>{totalPL >= 0 ? '+' : ''}{totalPL.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
          <Col span={4}><Card size="small"><b>持仓</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{positions.length} 条</span></Card></Col>
          <Col span={4}><Card size="small"><b>委托</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{orders.length} 条</span></Card></Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" items={[
          { key: 'accounts', label: `账户资金 (${filteredAccounts.length})`, children: (
            <div>
              <Input.Search placeholder="搜索客户端名称/ID" value={accountSearch} onChange={e => setAccountSearch(e.target.value)}
                style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
              <Table dataSource={filteredAccounts} columns={[
                { title: '客户端', dataIndex: 'client_id', width: 120, render: (v, r) => {
                  const name = r.client_name || getClientName(v);
                  return <Tag>{name === v ? v : `${name}(${v})`}</Tag>;
                }},
                { title: '总资产', dataIndex: 'total_asset', width: 100, render: v => <b>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b> },
                { title: '可用金额', dataIndex: 'available', width: 90, render: v => <span style={{ color: '#27ae60' }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
                { title: '股票市值', dataIndex: 'market_value', width: 90, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                { title: '持仓盈亏', dataIndex: 'profit_loss', width: 90, render: v => (
                  <span style={{ color: v >= 0 ? '#e74c3c' : '#27ae60' }}>{v >= 0 ? '+' : ''}{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                )},
                { title: '冻结', dataIndex: 'frozen', width: 70, render: v => v?.toLocaleString() },
                { title: '可取', dataIndex: 'withdrawable', width: 70, render: v => v?.toLocaleString() },
              ]} rowKey="id" size="small" pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }}
                locale={{ emptyText: '暂无账户数据' }} />
            </div>
          )},
          { key: 'positions', label: `持仓 (${filteredPositions.length})`, children: (
            <div>
              <Input.Search placeholder="搜索客户端ID/股票" value={posSearch} onChange={e => setPosSearch(e.target.value)}
                style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
              <Table dataSource={filteredPositions} columns={posColumns} rowKey="id" size="small"
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }} locale={{ emptyText: '暂无持仓数据' }} />
            </div>
          )},
          { key: 'orders', label: `当日委托 (${filteredOrders.length})`, children: (
            <div>
              <Input.Search placeholder="搜索客户端ID/股票" value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
              <Table dataSource={filteredOrders} columns={orderColumns} rowKey="id" size="small"
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }} locale={{ emptyText: '暂无委托数据' }} />
            </div>
          )},
        ]} />
      </Card>

      {/* 成员弹窗 */}
      <Modal title={`${isSuper ? '全部客户' : '我的分组'} - 客户列表`} open={showMemberModal}
        onCancel={() => setShowMemberModal(false)} footer={null} width={500}>
        <Input.Search placeholder="搜索客户端名称或ID" value={memberSearch}
          onChange={e => setMemberSearch(e.target.value)} style={{ marginBottom: 12 }} allowClear prefix={<SearchOutlined />} />
        <Table dataSource={myClientIds.filter(cid => {
          if (!memberSearch) return true;
          const s = memberSearch.toLowerCase();
          const c = allClients.find(x => x.id === cid);
          return cid.toLowerCase().includes(s) || (c?.name || '').toLowerCase().includes(s);
        }).map(cid => ({ cid, client: allClients.find(x => x.id === cid) }))} columns={[
          { title: '客户端ID', width: 180, render: (_, r) => <code>{r.cid}</code> },
          { title: '名称', render: (_, r) => r.client?.name || '-' },
          { title: '状态', width: 80, render: (_, r) => {
            const status = r.client?.status || 'offline';
            return <Badge status={status === 'online' ? 'success' : 'default'} text={status === 'online' ? '在线' : '离线'} />;
          }},
        ]} rowKey="cid" size="small" pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          locale={{ emptyText: '暂无客户' }} />
      </Modal>
    </div>
  );
}
